import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notificationActions } from "../../data/notification";
import useT from "../../i18n/useT";
import core from "../../core";

const statusStyle = (status) => {
    if (status === "approved") return { background: "#34c38f", color: "#fff" };
    if (status === "rejected") return { background: "#dc3545", color: "#fff" };
    return { background: "#ff9f0a", color: "#fff" };
};

const BalanceRequests = () => {
    const t = useT();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const isStaff = role === "admin" || role === "moderator";

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updates, setUpdates] = useState(0);
    const [form, setForm] = useState({ amount: "", note: "" });
    const [saving, setSaving] = useState(false);

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    useEffect(() => {
        setLoading(true);
        axios.get("/api/balance-requests")
            .then((res) => { setRequests(res.data.data || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [updates]);

    const submit = (e) => {
        e.preventDefault();
        if (form.amount === "" || Number(form.amount) <= 0) return;
        setSaving(true);
        axios.post("/api/balance-requests", form)
            .then((res) => { notify(res.data.message.en, "success-notification"); setForm({ amount: "", note: "" }); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"))
            .finally(() => setSaving(false));
    };

    const review = (id, status) => {
        axios.put(`/api/balance-requests/${id}`, { status })
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const statusLabel = (s) => t(`balance.${s}`);
    const money = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("balance.title")}</h2>

            {!isStaff && (
                <div className="cd-card" style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px", marginBottom: "20px", maxWidth: "520px" }}>
                    <h3 style={{ fontSize: "15px", marginBottom: "14px" }}>{t("balance.newRequest")}</h3>
                    <form onSubmit={submit} style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
                        <div style={{ flex: "1 1 140px" }}>
                            <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("balance.amount")}</label>
                            <input type="number" step="0.01" min="0" className="form-control" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} required />
                        </div>
                        <div style={{ flex: "2 1 200px" }}>
                            <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("balance.note")}</label>
                            <input type="text" className="form-control" value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} />
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={saving}>{t("balance.submit")}</button>
                    </form>
                </div>
            )}

            <h3 style={{ fontSize: "15px", margin: "6px 0 12px" }}>{isStaff ? t("balance.allRequests") : t("balance.myRequests")}</h3>

            {loading ? null : requests.length === 0 ? (
                <div style={{ opacity: 0.6 }}>{t("balance.none")}</div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="table" style={{ width: "100%" }}>
                        <thead>
                            <tr>
                                {isStaff && <th>{t("balance.dealer")}</th>}
                                <th>{t("balance.amount")}</th>
                                <th>{t("balance.note")}</th>
                                <th>{t("balance.status")}</th>
                                <th>{t("balance.date")}</th>
                                {isStaff && <th>{t("balance.actions")}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((r) => (
                                <tr key={r.id}>
                                    {isStaff && <td>{r.name ? `${r.name} ${r.surname || ""}` : r.username || r.user_id}</td>}
                                    <td>{money(r.amount)}</td>
                                    <td>{r.note || "—"}</td>
                                    <td><span style={{ ...statusStyle(r.status), padding: "2px 10px", borderRadius: "12px", fontSize: "12px" }}>{statusLabel(r.status)}</span></td>
                                    <td>{r.created_at ? core.formdatDate(r.created_at, "dd-mm-yyyy") : "—"}</td>
                                    {isStaff && (
                                        <td>
                                            {r.status === "pending" ? (
                                                <span style={{ display: "flex", gap: "6px" }}>
                                                    <button className="btn btn-sm btn-success" onClick={() => review(r.id, "approved")}>{t("balance.approve")}</button>
                                                    <button className="btn btn-sm btn-danger" onClick={() => review(r.id, "rejected")}>{t("balance.reject")}</button>
                                                </span>
                                            ) : (
                                                <span style={{ fontSize: "12px", opacity: 0.6 }}>{r.reviewed_by ? `${t("balance.reviewedBy")}: ${r.reviewed_by}` : "—"}</span>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default BalanceRequests;
